import { useQuery } from "@tanstack/react-query";
import { loansApi, vendorInvoicesApi } from "@/api/portfolioAdmin/client";
import { financialReportingApi } from "@/api/portfolioAdmin/financialReportingApi";
import type { ReportingDueStatus } from "@/api/portfolioAdmin/financialReportingApi";
import { loanTapePeriodApi } from "@/api/portfolioAdmin/pr6Client";
import { portfolioMetricsApi } from "@/api/portfolioAdmin/pr7Client";
import { formatPercent } from "@/components/portfolioAdmin/metrics/MetricsCards";
import { formatCurrency } from "@/design-system/lib/format";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelClearLine, PanelFigures, PanelRows } from "./shared";

/**
 * Portfolio Administration panels: loans assigned to me, VFI, financial
 * reporting, the monthly loan tape, and the cross-portfolio metrics
 * snapshot — each a compact card over the existing permission-gated
 * portfolio-admin endpoints.
 */

export function MyLoansWidget({ widget }: DashboardWidgetProps) {
  const loans = useQuery({
    queryKey: ["dashboard", "my-loans"],
    queryFn: () => loansApi.search({ assignedToMe: true }),
  });
  const rows = (loans.data ?? []).slice(0, 8);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/portfolio-admin/loans"
      isLoading={loans.isLoading}
      isError={loans.isError}
      isEmpty={rows.length === 0}
      emptyText="No loans are assigned to you right now."
    >
      <PanelRows
        items={rows.map((l) => ({
          id: l.id,
          primary: l.borrowerName ? `${l.loanNumber} — ${l.borrowerName}` : l.loanNumber,
          secondary: formatCurrency(l.currentBalance),
          badge: l.accountState,
          badgeTone: l.accountState === "performing" ? "ok" : "warn",
          href: `/portfolio-admin/loans/${l.id}`,
        }))}
      />
    </WidgetFrame>
  );
}

export function VfiDashboardWidget({ widget }: DashboardWidgetProps) {
  const vfi = useQuery({
    queryKey: ["dashboard", "vfi"],
    queryFn: () => vendorInvoicesApi.dashboard(),
  });
  const data = vfi.data;

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/portfolio-admin/vfi-accounting"
      isLoading={vfi.isLoading}
      isError={vfi.isError}
    >
      {data && (
        <PanelFigures
          figures={[
            {
              label: `outstanding (${data.openCount})`,
              value: formatCurrency(data.outstandingTotal),
              tone: data.outstandingTotal > 0 ? "warning" : undefined,
            },
            { label: "in transit", value: formatCurrency(data.inTransitTotal), tone: "info" },
            { label: "paid YTD", value: formatCurrency(data.paidYtd), tone: "ok" },
          ]}
        />
      )}
    </WidgetFrame>
  );
}

const DUE_TONE: Record<ReportingDueStatus, "neutral" | "warn" | "danger"> = {
  upcoming: "neutral",
  due: "warn",
  overdue: "danger",
};

export function ReportingOverviewWidget({ widget }: DashboardWidgetProps) {
  const overview = useQuery({
    queryKey: ["dashboard", "reporting-overview"],
    queryFn: () => financialReportingApi.portfolioOverview(),
  });
  const stats = overview.data?.stats;
  const dueItems = (overview.data?.dueItems ?? []).slice(0, 5);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/portfolio-admin/reporting"
      isLoading={overview.isLoading}
      isError={overview.isError}
    >
      {stats && (
        <div className="flex flex-col gap-space-3">
          <PanelFigures
            figures={[
              { label: "due this month", value: stats.dueThisMonth, tone: "warning" },
              { label: "overdue", value: stats.overdue, tone: "error" },
              { label: "completed YTD", value: stats.completedYtd, tone: "ok" },
            ]}
          />
          {dueItems.length === 0 ? (
            <PanelClearLine text="Nothing coming due." />
          ) : (
            <PanelRows
              items={dueItems.map((i) => ({
                id: `${i.seriesCode}-${i.periodStart}`,
                primary: `${i.seriesCode} — ${i.seriesTitle}`,
                badge: i.status,
                badgeTone: DUE_TONE[i.status],
                timestamp: i.dueDate,
              }))}
            />
          )}
        </div>
      )}
    </WidgetFrame>
  );
}

function currentTapePeriod(tz: string): string {
  return isoDateInTz(new Date(), tz).slice(0, 7);
}

export function LoanTapeStatusWidget({ widget }: DashboardWidgetProps) {
  const tz = useUserTimeZone();
  const period = currentTapePeriod(tz);
  const tape = useQuery({
    queryKey: ["dashboard", "loan-tape", period],
    queryFn: () => loanTapePeriodApi.listForPeriod({ period }),
  });
  const entries = tape.data ?? [];
  const received = entries.filter((e) => e.paymentReceivedAmount !== null).length;
  const variances = entries.filter((e) => e.varianceFlag).length;
  const locked = entries.filter((e) => e.lockedDate).length;

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle ?? period}
      to="/portfolio-admin/loan-tape"
      isLoading={tape.isLoading}
      isError={tape.isError}
      isEmpty={entries.length === 0}
      emptyText="No tape entries for this period yet."
    >
      <PanelFigures
        figures={[
          { label: "entries", value: entries.length },
          { label: "received", value: received, tone: "ok" },
          { label: "variances", value: variances, tone: "warning" },
          { label: "locked", value: locked },
        ]}
      />
    </WidgetFrame>
  );
}

export function PortfolioSnapshotWidget({ widget }: DashboardWidgetProps) {
  const composition = useQuery({
    queryKey: ["dashboard", "portfolio-composition"],
    queryFn: () => portfolioMetricsApi.composition(),
  });
  const performance = useQuery({
    queryKey: ["dashboard", "portfolio-performance"],
    queryFn: () => portfolioMetricsApi.performance(),
  });
  const c = composition.data;
  const p = performance.data;

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/portfolio-admin/metrics"
      toLabel="Full metrics"
      isLoading={composition.isLoading || performance.isLoading}
      isError={composition.isError && performance.isError}
    >
      <div className="flex flex-col gap-space-3">
        {c && (
          <PanelFigures
            figures={[
              { label: "active loans", value: c.activeLoanCount },
              {
                label: "outstanding principal",
                value: formatCurrency(c.aggregateOutstandingPrincipal),
              },
              { label: "avg loan size", value: formatCurrency(c.averageLoanSize) },
              { label: "wtd avg rate", value: formatPercent(c.weightedAverageInterestRate, 2) },
            ]}
          />
        )}
        {p && (
          <PanelFigures
            figures={[
              {
                label: "workout exposure",
                value: formatCurrency(p.totalWorkoutExposure),
                tone: "warning",
              },
              {
                label: "covenant pass (rolling)",
                value: formatPercent(p.covenantPassRateRolling, 0),
              },
              { label: "loss rate", value: formatPercent(p.lossRate, 2) },
            ]}
          />
        )}
      </div>
    </WidgetFrame>
  );
}
